import { useHistory, useLocation } from "react-router-dom";

import { useHandler } from "~/tools/use-handler";

import { useQueryViews } from "../../../../common/use-query-views";
import { type NavigateFromOptions, type NavigateWithinOptions, navigateFromLink } from "../core";
import { applyViewDiffMiddleware } from "../middlewares";
import type { HistoryOptions } from "../types";

export interface NavigateFromLink<
	TReturns extends {
		fromLink: unknown;
		withinLink: unknown;
	} = {
		fromLink: undefined | string;
		withinLink: string;
	},
	TExtraOptions = unknown,
> {
	/**
	 * Use it when it needs to change only search parameters of the current route.
	 *
	 * ---
	 *
	 * A function to navigate within the current route.
	 * This way it is only possible to assign the search params.
	 *
	 * @example
	 * const link = useNavigateFromLink("*", {
	 *  search: search => ({ ...search, search1: "value1" })
	 * });
	 *
	 * // === "/path?search1=value1" when the current location is "path/"
	 * // === "/path?search1=value1&search2=value2" when the current location is "/path?search2=value2"
	 */
	(route: "*", options: TExtraOptions & NavigateWithinOptions): TReturns["withinLink"];

	/**
	 * Use it when it needs to navigate within the given route or routes.
	 * It is useful when not all params of a route are known but the route is known, so this way it can apply params from the current location.
	 * For example when it needs to change a perspective on the current view, the only thing which should change is `mode` param.
	 *
	 * ---
	 *
	 * A function to navigate within the given route or routes.
	 *
	 * If the route has params specified as "path/:param1/:param2", the values of the params are passed to the `options.params`.
	 * If the route has no params, or params are optional, the `options.params` is not required.
	 *
	 * The search might be modified via `options.search`. If the `options.search` is not defined, the search will be empty.
	 *
	 * Noting happens if the given route does not match the current location.
	 *
	 * @param routes - a route or an array of routes to navigate from. The first matched route will be used.
	 * @param options - the options to navigate within the route.
	 *
	 * @example
	 * const link = useNavigateFromLink("/path/:param1/:param2(a|b)", {
	 *   params: params => ({ ...params, param1: "value1" }),
	 *   search: search => ({ ...search, search1: "value1" })
	 * });
	 * // === "/path/value1/a?search1=value1" when the current location is "/path/value2/a"
	 */
	<TRoute extends string>(
		routes: TRoute | Array<TRoute> | ReadonlyArray<TRoute>,
		options: TExtraOptions & NavigateFromOptions<TRoute>,
	): TReturns["fromLink"];
}

/**
 * A hook that implements `NavigateFromLink`.
 *
 * @see NavigateFromLink
 */
export const useNavigateFromLink: NavigateFromLink = (route: string, options: NavigateFromOptions<string>) => {
	const location = useLocation();
	const views = useQueryViews();

	return navigateFromLink(
		{
			location,
			middleware: views ? [applyViewDiffMiddleware(views)] : [],
		},
		route,
		options,
	)!;
};

/**
 * A hook that returns `NavigateFromLink` function.
 *
 * @see NavigateFromLink
 * @returns NavigateFromLink
 */
export const useNavigateFromLinkLazy = (): NavigateFromLink => {
	const history = useHistory();
	const views = useQueryViews();

	return (route: string, options: NavigateFromOptions<string>) =>
		navigateFromLink(
			{
				location: history.location,
				middleware: views ? [applyViewDiffMiddleware(views)] : [],
			},
			route,
			options,
		)!;
};

export type NavigateFrom = NavigateFromLink<
	{
		fromLink: void;
		withinLink: void;
	},
	HistoryOptions
>;

/**
 * A hook to navigate from the given route to the given route
 *
 * @see NavigateFromLink
 * @returns NavigateFrom
 */
export const useNavigateFrom = (): NavigateFrom => {
	const history = useHistory();
	const navigateFromLinkLazy = useNavigateFromLinkLazy();

	return useHandler((route: string, { method = "push", ...options }: HistoryOptions & NavigateFromOptions<string>) => {
		const url = navigateFromLinkLazy(route, options);

		if (url == null || url === `${history.location.pathname}${history.location.search}`) {
			return;
		}

		if (method === "push") {
			history.push(url);
		} else if (method === "replace") {
			history.replace(url);
		}
	});
};
